import React, { JSX } from 'react';
import * as Sentry from "@sentry/react";
import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { DialogTitle, DialogActions, Button, Select,
  SelectChangeEvent, MenuItem, Box,
  TextField, Alert, ToggleButtonGroup, ToggleButton, Typography,
  useTheme, Stack,
  useMediaQuery} from '@mui/material';
import QueryCodeEditorPanel from 'components/queries/QueryCodeEditorPanel';
import QueryBuilderPanel from 'components/queries/QueryBuilderPanel';
import { useTranslation } from 'react-i18next';
import { Query, defaultQuery, mergeQueries } from 'system/query/Query';
import useFetchData from 'system/useFetchData';
import { QueryInfo } from 'system/query/QueryInfo';
import { FetchWrapper } from 'components/apiHandling/FetchWrapper';
import SavedQueryDialog from './savedQueries/SavedQueryDialog';
import DatasetSelectionButton from 'components/resources/datasets/DatasetSelectionButton';
import { enqueueSnackbar } from 'notistack';
import ConfirmationDialog from 'components/dialogs/ConfirmationDialog';
import APIRequest, { APIError, removeCachesMatching  } from 'system/APIRequest';
import ResponsiveDialog from 'components/dialogs/ResponsiveDialog';
import { QueryDrilldownDialog } from 'components/dialogs/QueryDrilldown/QueryDrilldownDialog';
import Chip from 'components/uiElements/dataDisplay/Chip';
import { Tag } from 'routes/tagManagement/TagManagementPage';
import TagRequestHelper from 'routes/tagManagement/TagRequestHelper';
import TagSelector from 'routes/tagManagement/TagSelector';
import ColorSelector from 'components/uiElements/inputs/colorSelector/ColorSelector';
import { AnyColor } from 'react-colorful/dist/types';

export interface QueryDialogProps {
  query: Query;

  fixedDatasetId?: string;

  hideQueryTitle?: boolean;

  onSubmit: (queryId: string) => void;
  isOpen: boolean;
  onClose?: () => void;
  title?: string;
}

export enum editorViews {
  QuickStart = "quick-start",
  Builder = "builder",
  CodeEditor = "code-editor"
}

interface QueryConfirmationType {
  isUntitled?: boolean,
  hasMatchingTitle?: boolean,
  willOverwritingExisting?: boolean,
  queryNotModifiable?: boolean
}

export default function QueryDialog(props: QueryDialogProps): JSX.Element {
  const { i18n } = useTranslation();
  const theme = useTheme();
  const isMediumScreen = useMediaQuery(theme.breakpoints.down("md"));
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const editorRef = useRef(null);

  const [storedQuery, setStoredQuery] = useState<Query | undefined>(undefined);

  const [query, setQuery] = useState<Query>(() => props.query);
  const [queryColor, setQueryColor] = useState<AnyColor | undefined>(props.query.meta?.color);
  const [previewQuery, setPreviewQuery] = useState<Query | undefined>(undefined);

  const [editorView, setEditorView] = useState<editorViews | null>(null);
  const [savedQueryDialogIsOpen, setSavedQueryDialogIsOpen] = useState<boolean>(false);

  const [queryConfirmation, setQueryConfirmation] = useState<QueryConfirmationType | undefined>(undefined);

  const [closeDialogConfirmation, setCloseDialogConfirmation] = useState<boolean>(false);

  const [validationError, setValidationError] = useState<APIError | undefined>(undefined);

  const [saveAsNewQuery, setSaveAsNewQuery] = useState<boolean>(false);

  const [queryInfoReponse, queryInfoIsLoading, queryInfoError, ] = useFetchData(query?.dataset_id && `query-info/${query?.dataset_id}`, [query?.dataset_id]);
  const queryInfo:QueryInfo = useMemo(() => queryInfoReponse && new QueryInfo(queryInfoReponse), [queryInfoReponse]);

  const [isAddingFirstTag, setIsAddingFirstTag] = useState<boolean>(false);

  const confirmText = useMemo(() => {
    if (queryConfirmation?.willOverwritingExisting || queryConfirmation?.queryNotModifiable) {
      return saveAsNewQuery ? i18n.t("query-form.save-as-new") : i18n.t("query-form.save-over-existing");
    }
    return i18n.t("save");
  }, [queryConfirmation?.willOverwritingExisting, queryConfirmation?.queryNotModifiable, saveAsNewQuery, storedQuery]);

  // If the form is editing an existing query, load the query from the server for comparison and validation
  useEffect(() => {
    // Check if the query is a new query or an existing query
    if (props.query?.id && props.query?.id !== storedQuery?.id) {
      APIRequest(`obj/query/${props.query.id}`, {include_deleted:"1"}).then((response) => {
        setStoredQuery(new Query(response.output));
        if (!response.output._modifiable || response.output._deleted) {
          setSaveAsNewQuery(true);
          setQueryConfirmation({queryNotModifiable: true});
        } else {
          setSaveAsNewQuery(false);
          setQueryConfirmation(undefined);
        }
      });
    }
  }, [props.query?.id]);

  useEffect(() => {
    const fixedDatasetId = props.fixedDatasetId;
    if (fixedDatasetId && query?.dataset_id !== fixedDatasetId ) {
      setQuery(new Query({...query, dataset_id: fixedDatasetId}));
    }
  }, [props.fixedDatasetId, query]);

  function setCurrentDatasetId(datasetId:string | undefined) {
    if (datasetId && query && query.dataset_id !== datasetId) {
      setQuery(new Query({...query, dataset_id: datasetId}));
    }
  }

  // If editorView is null, use the query to determine the appropriate view
  // let currentEditorView:editorViews = editorView || (props.query ? editorViews.CodeEditor : editorViews.QuickStart);
  const currentEditorView:editorViews = editorView || editorViews.Builder;

  const content = JSON.stringify(query?.query || "{}",undefined, 2);

  // Save the query via the API, depending on whether it is a new query or an existing query
  const saveQuery = (query:Query) => {
    if (!query.id) {
      APIRequest("obj/query", {}, {
        method: "POST",
        "body": JSON.stringify(query, undefined, 2)
      }).then(async (response) => {
        await saveTags(response.id);
        props.onSubmit(response.id);
      }).catch((error: any) => {
        enqueueSnackbar(`${i18n.t('query-form.error-saving-query')}: ${error.message}`, {variant: 'error', persist: true});
      });
    } else {
      const queryId = query.id;
      const savableQuery = new Query({
        name: query.name,
        dataset_id: query.dataset_id,
        meta: query.meta,
        query: query.query,
        prompt: query.prompt ? query.prompt : ""
      });
      APIRequest(`obj/query/${queryId}`, {}, {
        method: "PUT",
        "body": JSON.stringify(savableQuery, undefined, 2)
      }).then(async (response) => {
        removeCachesMatching(queryId);
        await saveTags(queryId);
        props.onSubmit(queryId);
      }).catch((error: any) => {
        enqueueSnackbar(`${i18n.t('query-form.error-saving-query')}: ${error.message}`, {variant: 'error', persist: true});
      });
    }
  };

  const submitQuery = (saveAsNewQuery:boolean = false) => {
    // If the query has no name, set a default name
    if (!props.hideQueryTitle && !query.name || !query.name.length || query.name === "untitled") {
      query.name = i18n.t("query-form.default-name") || "untitled";
    }

    // Limit the name length to 256 characters, which is the maximum length for query names via the API.
    if (query.name && query.name.length >= 256) {
      query.name = query.name.substring(0, 256);
    }

    // Assign the query color
    const newQueryMeta = {...query.meta};
    if (queryColor) {
      newQueryMeta.color = queryColor;
    } else {
      if (newQueryMeta.color) {
        delete newQueryMeta.color;
      }
    }

    const savableQuery = new Query({
      name: query.name,
      dataset_id: query.dataset_id,
      meta: newQueryMeta,
      query: query.query,
      prompt: query.prompt
    });

    if (!saveAsNewQuery && storedQuery?.id) {
      savableQuery.id = storedQuery.id;
    }

    if (currentEditorView === editorViews.CodeEditor) {
      const editor:any = editorRef?.current;
      let queryContent:any = editor?.getContent();
      if (!queryContent || !queryContent.length) {
        saveQuery(query);
        return;
      }
      try {
        queryContent = JSON.parse(queryContent);
        const newQuery = new Query({
          name: savableQuery.name,
          dataset_id: savableQuery.dataset_id,
          meta: newQueryMeta,
          query: queryContent,
          prompt: savableQuery.prompt ? savableQuery.prompt : ""
        });
        if (!saveAsNewQuery && storedQuery?.id) {
          newQuery.id = query.id;
        }
        saveQuery(newQuery);
      } catch (e) {
        enqueueSnackbar(i18n.t("query-form.error-parsing-query-content"), { variant: "error", persist: true });
      }
    } else {
      saveQuery(savableQuery);
    }
  }

  const checkForConfirmation = () => {
    let needsConfirmation:boolean = false;
    const queryValidation:QueryConfirmationType = {};

    if (!props.hideQueryTitle) {
      if (!query.name || !query.name.length || query.name === "New Query" || query.name === "untitled") {
        needsConfirmation = true;
        queryValidation.isUntitled = true;
      } else if (storedQuery && storedQuery.name === query.name) {
        needsConfirmation = true;
        queryValidation.hasMatchingTitle = true;
      }
    }

    if (storedQuery && storedQuery?.id) {
      queryValidation.willOverwritingExisting = true;
      needsConfirmation = true;
    }

    if (storedQuery && !storedQuery?._modifiable) {
      queryValidation.queryNotModifiable = true;
      needsConfirmation = true;
    }

    if (needsConfirmation) {
      return queryValidation;
    }
  }

  const [initialTags, setInitialTags] = useState<Array<Tag>>([]);
  const [currentTags, setCurrentTags] = useState<Array<Tag>>([]);

  async function loadTags() {
    if (query.id) {
      const result = await TagRequestHelper.getTagsForQuery(query.id);
      setInitialTags(result.tags);
      setCurrentTags(result.tags);
    }
  }

  async function saveTags(queryId: string): Promise<void> {
    const addedTags = currentTags.filter((currentTag) => !initialTags.find((initialTag) => initialTag.id === currentTag.id));
    const removedTags = initialTags.filter((initialTag) => !currentTags.find((currentTag) => currentTag.id === initialTag.id));

    const requests = [];
    for (let tag of addedTags) {
      const promise = new Promise(async (resolve, reject) => {
        const response = await TagRequestHelper.addTagToQuery(tag.id, queryId);
        resolve(response);
      });
      requests.push(promise);
    }
    for (let tag of removedTags) {
      const promise = new Promise(async (resolve, reject) => {
        const response = await TagRequestHelper.removeTagFromQuery(tag.id, queryId);
        resolve(response);
      });
      requests.push(promise);
    }

    await Promise.all(requests);
  }

  function handleTagSelectorChange(selectedTags: Array<Tag>): void {
    setCurrentTags(selectedTags);
  }

  useEffect(() => {
    loadTags();
  }, []);

  let maxTagsDisplayed = 7;
  if (isSmallScreen) maxTagsDisplayed = 2;
  else if (isMediumScreen) maxTagsDisplayed = 3;
  const hasTags = currentTags.length > 0;
  const queryTitleValue = props.hideQueryTitle ? i18n.t('query') : props.title || `Query ${i18n.t('title')} :`

  const queryTitleComponent = (
    <Stack direction="row" sx={{
      alignItems: "center"
    }}>
      <Box sx={{
        marginRight: 1
      }}>
        <ColorSelector
          color={queryColor}
          onChange={(color) => {
            setQueryColor(color);
          }}
          showInput
        />
      </Box>
      <Box sx={{
        width: "fit-content"
      }}>
        <Typography noWrap variant="h6">{queryTitleValue}</Typography>
      </Box>
      <TextField
        autoComplete="off"
        fullWidth
        slotProps={{
          input: {
            sx: {
              "& .MuiInputBase-input": {
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap"
              }
            }
          }
        }}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
          setQuery(new Query({...query, name: event.target.value}));
        }}
        sx={{pl:1}}
        value={query.name || ""}
        variant="standard"
      />
    </Stack>
  );
  const datasetComponent = (
    <Stack direction="row" sx={{
      alignItems: "center"
    }}>
      <Box sx={{
        width: "fit-content"
      }}>
        <Typography noWrap variant="h6">{i18n.t("dataset")} : &nbsp;</Typography>
      </Box>
      <DatasetSelectionButton
        size="small"
        selectedDatasetId={query.dataset_id || ""}
        setSelectedDatasetId={(datasetId:string | undefined) => {
          if (datasetId) {
            if (datasetId !== query.dataset_id) {
              setCurrentDatasetId(datasetId);
              setQuery(new Query({ ...defaultQuery, name: query.name || i18n.t("query-form.default-name"), dataset_id: datasetId}));
            }
          }
        }}
      />
    </Stack>
  );

  const dialogTitle: ReactNode = (
    <Stack sx={{
      gap: 0.5
    }}>
      <Stack
        direction="row"
        sx={{
          alignItems: "center",
          gap: 1
        }}>
        <Box sx={{
          flex: 1
        }}>
          {queryTitleComponent}
        </Box>
        {!hasTags && <TagSelector selectedTags={currentTags} onClose={handleTagSelectorChange} />}
        <Box sx={{flex: 1, justifyItems: "end"}}>
          {datasetComponent}
        </Box>
      </Stack>
      {hasTags &&
      <Stack
        direction="row"
        sx={{
          overflow: "hidden",
          gap: 0.5
        }}>
        {currentTags.map((tag, index) => {
          if (index+1 > maxTagsDisplayed) {
            return null;
          }
          return <Chip key={`QueryDialog.tag.${index}`} label={tag.name} color={tag.color} />;
        })}
        {currentTags.length > maxTagsDisplayed &&
          <Typography
            variant="h6"
            sx={{
              alignSelf: "center",
              paddingRight: 0.5
            }}>{`+${currentTags.length - maxTagsDisplayed} Tag${currentTags.length - maxTagsDisplayed > 1 ? "s" : ""}`}</Typography>
        }
        <TagSelector selectedTags={currentTags} onClose={handleTagSelectorChange} />
      </Stack>
      }
    </Stack>
  );

  return (
    <ResponsiveDialog
      open={props.isOpen}
      onClose={() => {
        if (!closeDialogConfirmation) {
          setCloseDialogConfirmation(true);
          return false;
        }
      }}
      maxWidth="md"
      sx={{ margin:{xs:0, sm:0} }}
    >
    <DialogTitle sx={{zIndex:1}}>
      {dialogTitle}
    </DialogTitle>
      {!query.dataset_id &&
        <Alert severity="info" sx={{m:4}}>
          <Box sx={{mb:2}}>{i18n.t("query-form.select-dataset-warning")}</Box>
          <DatasetSelectionButton
            size="small"
            selectedDatasetId={query.dataset_id || ""}
            setSelectedDatasetId={(datasetId:string | undefined) => {
              if (datasetId) {
                if (datasetId !== query.dataset_id) {
                  setCurrentDatasetId(datasetId);
                  setQuery(new Query({ ...defaultQuery, name: i18n.t("query-form.default-name"), dataset_id: datasetId}));
                }
              }
            }} />
        </Alert>
      }
      {query.dataset_id &&
        <FetchWrapper isLoading={queryInfoIsLoading} fetchError={queryInfoError}>
          {currentEditorView === editorViews.Builder &&
            <QueryBuilderPanel
              query={query}
              queryInfo={queryInfo}
              setQuery={setQuery}
              size={hasTags || isAddingFirstTag ? "medium" : "small"}
            />
          }
          {currentEditorView === editorViews.CodeEditor &&
            <QueryCodeEditorPanel
              height= {500}
              content={content || ""}
              ref={editorRef}
            />
          }
        </FetchWrapper>
      }
    <DialogActions sx={{backgroundColor: theme.palette.background.paper, zIndex:1}}>
      <Button
        variant="contained"
        onClick={() => setSavedQueryDialogIsOpen(true)} >
        {i18n.t("query-form.load-saved-query")}
      </Button>
      <Button
        variant="text"
        color="error"
        onClick={() => setQuery(new Query({...defaultQuery, dataset_id: query.dataset_id || props.fixedDatasetId || ""}))}
        >
        {i18n.t("widget-types.template-search.clear-query-form")}
      </Button>
      <div style={{flex: '1 0 0'}} />
      <Button
        variant="text"
        color="info"
        onClick={() => setPreviewQuery(new Query({query: query.query, dataset_id: query.dataset_id || props.fixedDatasetId || "", name: query.name || i18n.t("query-form.default-name")}))}
        >
        {i18n.t("query-form.preview-query")}
      </Button>
      <div style={{flex: '1 0 0'}} />
      {savedQueryDialogIsOpen &&
      <SavedQueryDialog
        isOpen={savedQueryDialogIsOpen}
        allowMultipleSelection={true}
        onInsert={(newQueries:Query[]) => {
          const queriesToMerge = newQueries;
          // If we don't have an established query (with a dataset),
          // This is really the same as a "Replace" action.
          if (query.query && query.dataset_id) {
            queriesToMerge.unshift(query);
          }
          const mergedQuery = mergeQueries(queriesToMerge, {flatten: true});
          setQuery(mergedQuery);
          setSavedQueryDialogIsOpen(false);
          loadTags();
        }}
        onReplace={(newQueries:Query[]) => {
          const mergedQuery = mergeQueries([...newQueries], {flatten: true});
          setQuery(mergedQuery);
          setStoredQuery(mergedQuery);
          setSavedQueryDialogIsOpen(false);
          loadTags();
        }}
        onClose={() => {
          setSavedQueryDialogIsOpen(false);
        }}
      />
      }
      <Select
        labelId="editor-view-select-label"
        id="editor-view-select"
        sx={{ p: 0, margin:"0 30px 0 0" }}
        variant="standard"
        value={currentEditorView}
        label="Age"
        onChange={(event: SelectChangeEvent<editorViews>, child: ReactNode) => {
          // If switching from the code editor, try to parse the contents
          if (currentEditorView === editorViews.CodeEditor) {
            const editor:any = editorRef?.current;
            let queryContent:any = editor?.getContent();
            try {
              if (queryContent) {
                queryContent = JSON.parse(queryContent);
                setQuery(new Query({query: queryContent, dataset_id: query.dataset_id}));
              }
              setEditorView(event.target.value as editorViews);
            } catch (e) {
              console.error(`Error parsing queryContent: ${e}`);
              Sentry.captureException(e);
            }
          } else if (currentEditorView !== event.target.value) {
            setEditorView(event.target.value as editorViews);
          }
        }} >
        <MenuItem value={editorViews.Builder}>
          {i18n.t('query-form.builder')}
        </MenuItem>
        <MenuItem value={editorViews.CodeEditor}>
          {i18n.t('query-form.code-editor')}
        </MenuItem>
      </Select>
      <Button
        variant="outlined"
        onClick={(event: React.MouseEvent) => {
          event.stopPropagation();
          event.preventDefault();
          props.onClose && props.onClose(); }}>
        {i18n.t('cancel')}
      </Button>
      <Button
        disabled={!query.dataset_id}
        variant="contained"
        onClick={() => {
          APIRequest("query/test", {"validate_runnable": 0}, {
            method: "POST",
            body: JSON.stringify(query)
          }, false, 3, [400]).then(() => {
            const queryConfirmation = checkForConfirmation();
            if (queryConfirmation) {
              setQueryConfirmation(queryConfirmation);
            } else {
              enqueueSnackbar(i18n.t("query-form.query-validated"), { variant: "success" });
              submitQuery(saveAsNewQuery);
            }
          }).catch((error:APIError) => {
            setValidationError(error);
          });
        }}>
        {i18n.t('submit')}
      </Button>
    </DialogActions>
      {/* if there is a validation error, show a dialog with the error message */}
      <ConfirmationDialog
        hideCancel={true}
        isOpen={validationError !== undefined}
        title={i18n.t("query-form.validation-error-dialog-title")}
        onClose={() => { setValidationError(undefined); props.onClose && props.onClose(); }} >
        <Alert severity="error">
          {validationError?.server_message}
        </Alert>
      </ConfirmationDialog>
      {/* if the user tries to close the dialog by clicking outside or hitting ESC, show a simple confirmation */}
      <ConfirmationDialog
        isOpen={closeDialogConfirmation}
        title={i18n.t("warning")}
        description={i18n.t("query-form.close-dialog-confirmation")}
        onClose={(wasConfirmed) => {
          setCloseDialogConfirmation(false);
          if (props.onClose && wasConfirmed) {
            props.onClose();
          }
        }}/>
      {/* Comprehensive confirmation dialog on save */}
      {/* if the user tries to submit a query with a missing or matching title, or will overwrite or cannot overwrite a query */}
      <ConfirmationDialog
        isOpen={!!queryConfirmation}
        title={i18n.t("query-form.confirmation-dialog-title")}
        confirmText={confirmText}
        onClose={(wasConfirmed) => {
          if (wasConfirmed) {
            submitQuery(saveAsNewQuery);
          }
          setQueryConfirmation(undefined);
        }}
      >
        {queryConfirmation?.isUntitled &&
          <Alert severity="info">
            {i18n.t("query-form.untitled-query-warning")}
          </Alert>
        }
        {queryConfirmation?.hasMatchingTitle &&
          <Alert severity="info">
            {i18n.t("query-form.matching-title-warning")}
          </Alert>
        }
        {(queryConfirmation?.isUntitled || queryConfirmation?.hasMatchingTitle) &&
          <>
            <Typography sx={{pt:2, pb:"3px"}}>
              {i18n.t("query-title")}:
            </Typography>
            <TextField
              autoComplete="off"
              variant="outlined"
              sx={{mb:2, width: "100%"}}
              value={query.name || ""}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                setQuery(new Query({...query, name: event.target.value}));
              }} />
          </>
        }

        {queryConfirmation?.willOverwritingExisting &&
          <>
          <Alert severity="info">
            {i18n.t("query-form.overwrite-warning")}
          </Alert>
          <Box sx={{mb:2, mt:2, textAlign:"center"}}>
            <ToggleButtonGroup
              color="primary"
              value={saveAsNewQuery ? "new" : "existing"}
              exclusive
              onChange={(event: React.MouseEvent<HTMLElement>, value: string) => {
                if (value === "new") {
                  setSaveAsNewQuery(true);
                } else {
                  setSaveAsNewQuery(false);
                }
              }}
            >
              <ToggleButton value="existing">
                {i18n.t("query-form.save-over-existing")}
              </ToggleButton>
              <ToggleButton value="new">
                {i18n.t("query-form.save-as-new")}
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
          </>
        }

        {queryConfirmation?.queryNotModifiable &&
          <Alert severity="info">
            {i18n.t("query-form.query-not-modifiable-warning")}
          </Alert>
        }
        </ConfirmationDialog>
        {!!(previewQuery && queryInfo) &&
          <QueryDrilldownDialog
            title={previewQuery.name}
            queryInfo={queryInfo}
            onClose={() => setPreviewQuery(undefined)}
            activeQueryIds={[]}
            drilldownQuery={previewQuery}
            hideDashboardDrilldown={true}
          />
        }
    </ResponsiveDialog>
  );
}
